import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import { reportError } from '~/utils/reportError';

import type { CapacityRoomRate } from '../CapacityStep/utils';
import { linkPackages } from '../PackageSetupStep/linkPackages';
import { getPackagePersistId } from '../PackageSetupStep/utils';
import type { FlashRatePlanDraft } from '../RatePlanStep/utils';
import type { RoomRateRoom } from '../RoomRatesStep/utils';
import type { FlashOfferRoomDraft } from '../RoomsStep/utils';
import { buildTaxesAndFeesUpdatePayload, mapSavedTaxFee, mapTaxSettings } from '../TaxesFeesStep/formData';
import type { SavedPropertyTaxes, TaxesAndFeesUpdatePayload } from '../TaxesFeesStep/formData';
import type { AppliedFlashRoomRate } from '../types';

import type { SubmissionSectionKey } from './buildDraftSummary';
import type {
  CreatedFlashResources,
  PreExistingPackageSnapshot,
  PreExistingTaxesFeesSnapshot,
  RollbackErrorEntry,
} from './rollbackCreatedFlashResources';
import { rollbackCreatedFlashResources } from './rollbackCreatedFlashResources';

export interface SubmitFlashOfferDraftParams {
  createRatePlansFromDrafts: () => Promise<{
    createdRatePlans?: Array<App.RatePlan>;
    errorMessage?: string;
    nextDrafts: Array<FlashRatePlanDraft>;
    success: boolean;
  }>;
  createSelectedRooms: () => Promise<{
    errorMessage?: string;
    savedRoomsByDraftId: Map<string, Pick<App.RoomType, 'id' | 'name'>>;
    success: boolean;
  }>;
  flexibleNightsById: Record<string, boolean>;
  getProperty: (propertyId: string) => Promise<{ result: unknown }>;
  handleApplyDefaultCapacities: (roomRates: Array<CapacityRoomRate>) => Promise<{ failedRoomRateIds: Array<string> }>;
  handleApplyRoomRates: (overrides?: {
    createdRatePlans?: Array<App.RatePlan>;
    selectedRoomRatePlanIds?: Record<string, Array<string>>;
  }) => Promise<{
    createdRoomRates: Array<AppliedFlashRoomRate>;
    errorMessage?: string;
    reusedRoomRates: Array<AppliedFlashRoomRate>;
    success: boolean;
  }>;
  offerId: string;
  /** Package id -> room: a real room type id, or a not-yet-created draft room's key. */
  packageRoomTypeIds: Record<string, string>;
  propertyId: string;
  roomDrafts: Array<FlashOfferRoomDraft>;
  roomRateRooms: Array<RoomRateRoom>;
  salesforceRooms: Array<SalesforceRoom>;
  selectedRoomRatePlanIds: Record<string, Array<string>>;
  taxesFeesDraft: TaxesAndFeesUpdatePayload | null;
  updatePropertyTaxesAndFees: (payload: TaxesAndFeesUpdatePayload, propertyId: string) => Promise<unknown>;
  wasPropertyCreatedThisSession: boolean;
}

export type SubmissionStage = Exclude<SubmissionSectionKey, 'property'>;

export interface SubmitFlashOfferDraftResult {
  errorMessage?: string;
  failedStage?: SubmissionStage;
  packageLinkFailures?: Array<string>;
  flexibleNightsBlockedCount?: number;
  packageUnmatchedCount?: number;
  updateFailurePackageIds?: Array<string>;
  rollbackErrors?: Array<RollbackErrorEntry>;
  success: boolean;
}

function resolveRoomKey(roomKey: string, roomKeyToRealId: Record<string, string>) {
  return roomKeyToRealId[roomKey] ?? roomKey;
}

function resolveSelectedRoomRatePlanIds(
  selectedRoomRatePlanIds: Record<string, Array<string>>,
  roomKeyToRealId: Record<string, string>,
  ratePlanDraftIdToRealId: Record<string, string>,
): Record<string, Array<string>> {
  const resolved: Record<string, Array<string>> = {};

  Object.entries(selectedRoomRatePlanIds).forEach(([roomKey, ratePlanDraftIds]) => {
    const realRatePlanIds = ratePlanDraftIds
      .map((draftId) => ratePlanDraftIdToRealId[draftId])
      .filter((id): id is string => !!id);

    if (realRatePlanIds.length > 0) {
      resolved[resolveRoomKey(roomKey, roomKeyToRealId)] = realRatePlanIds;
    }
  });

  return resolved;
}

async function buildTaxesFeesSnapshot(
  propertyId: string,
  getProperty: SubmitFlashOfferDraftParams['getProperty'],
): Promise<PreExistingTaxesFeesSnapshot> {
  const response = await getProperty(propertyId);
  const loadedProperty = response.result as SavedPropertyTaxes;
  const existingItems = (loadedProperty.taxes_and_fees ?? []).map(mapSavedTaxFee);

  return {
    previousPayload: buildTaxesAndFeesUpdatePayload({
      ...mapTaxSettings(loadedProperty),
      taxes_and_fees: existingItems,
    }),
    propertyId,
  };
}

function buildPackageSnapshots(
  matchedPackages: Array<App.Package>,
  offerId: string,
): Array<PreExistingPackageSnapshot> {
  return matchedPackages.flatMap((pkg) => {
    const packageId = getPackagePersistId(pkg);

    return packageId
      ? [
          {
            offerId,
            packageId,
            previousFkPropertyId: ('fk_property_id' in pkg && pkg.fk_property_id) || null,
            previousFkRoomRateId: ('fk_room_rate_id' in pkg && pkg.fk_room_rate_id) || null,
            previousFkRoomTypeId: ('fk_room_type_id' in pkg && pkg.fk_room_type_id) || null,
            previousFlexibleNights: 'flexible_nights' in pkg && !!pkg.flexible_nights,
          },
        ]
      : [];
  });
}

/**
 * Atomically creates every resource the CM drafted across the wizard, in dependency order
 * (rooms -> rate plans -> room rates -> capacities -> taxes & fees -> package flexible-nights ->
 * real package linking), resolving each step's draft ids to the real ones the previous step just
 * created. Any failure -- including a capacity failure, since a room rate without capacity can't
 * appear on the front end -- rolls back everything created/changed so far and returns.
 */
export async function submitFlashOfferDraft({
  createRatePlansFromDrafts,
  createSelectedRooms,
  flexibleNightsById,
  getProperty,
  handleApplyDefaultCapacities,
  handleApplyRoomRates,
  offerId,
  packageRoomTypeIds,
  propertyId,
  roomDrafts,
  roomRateRooms,
  salesforceRooms,
  selectedRoomRatePlanIds,
  taxesFeesDraft,
  updatePropertyTaxesAndFees,
  wasPropertyCreatedThisSession,
}: SubmitFlashOfferDraftParams): Promise<SubmitFlashOfferDraftResult> {
  const createdResources: CreatedFlashResources = { ratePlanIds: [], roomRates: [], roomTypeIds: [] };

  async function rollback(taxesFeesSnapshot?: PreExistingTaxesFeesSnapshot) {
    const { errors } = await rollbackCreatedFlashResources({
      createdResources,
      packageSnapshots: [],
      propertyId,
      taxesFeesSnapshot,
    });

    return errors;
  }

  const roomsResult = await createSelectedRooms();
  const roomKeyToRealId: Record<string, string> = {};

  roomDrafts.forEach((draft) => {
    const saved = roomsResult.savedRoomsByDraftId.get(draft.draftId);

    if (saved) {
      roomKeyToRealId[draft.draftId] = saved.id;
      createdResources.roomTypeIds.push(saved.id);
    } else if (draft.id) {
      roomKeyToRealId[draft.draftId] = draft.id;
    }
  });

  if (!roomsResult.success) {
    const rollbackErrors = await rollback();
    return {
      errorMessage: roomsResult.errorMessage ?? 'Unable to create rooms.',
      failedStage: 'rooms',
      rollbackErrors,
      success: false,
    };
  }

  const ratePlanResult = await createRatePlansFromDrafts();
  const ratePlanDraftIdToRealId: Record<string, string> = {};

  ratePlanResult.nextDrafts.forEach((draft) => {
    if (draft.createdRatePlanId) {
      ratePlanDraftIdToRealId[draft.draftId] = draft.createdRatePlanId;
    }
  });
  // Tracked from `createdRatePlans`, not `nextDrafts` -- a pre-flight failure leaves `nextDrafts`
  // unchanged, which can still carry a real id left from an earlier, rolled-back attempt.
  (ratePlanResult.createdRatePlans ?? []).forEach((ratePlan) => {
    if (ratePlan.id) {
      createdResources.ratePlanIds.push(ratePlan.id);
    }
  });

  if (!ratePlanResult.success) {
    const rollbackErrors = await rollback();
    return {
      errorMessage: ratePlanResult.errorMessage ?? 'Unable to create rate plans.',
      failedStage: 'ratePlans',
      rollbackErrors,
      success: false,
    };
  }

  const resolvedSelection = resolveSelectedRoomRatePlanIds(
    selectedRoomRatePlanIds,
    roomKeyToRealId,
    ratePlanDraftIdToRealId,
  );
  const roomRatesResult = await handleApplyRoomRates({
    createdRatePlans: ratePlanResult.createdRatePlans,
    selectedRoomRatePlanIds: resolvedSelection,
  });

  // Room rates created so far must roll back even on this step's own failure -- applyRoomRates
  // stops at the first failure but keeps whatever it already created.
  createdResources.roomRates.push(
    ...roomRatesResult.createdRoomRates.map((roomRate) => ({
      roomRateId: roomRate.roomRateId,
      roomTypeId: roomRate.roomTypeId,
    })),
  );

  if (!roomRatesResult.success) {
    const rollbackErrors = await rollback();
    return {
      errorMessage: roomRatesResult.errorMessage ?? 'Unable to apply room rates.',
      failedStage: 'roomRates',
      rollbackErrors,
      success: false,
    };
  }

  // A room rate without capacity can't appear on the front end, so this fails like any other
  // stage -- rollback deletes the room rates, cascading away any partial capacity rows with them.
  const capacityResult = await handleApplyDefaultCapacities(roomRatesResult.createdRoomRates);

  if (capacityResult.failedRoomRateIds.length > 0) {
    const rollbackErrors = await rollback();
    return {
      errorMessage: `Unable to apply default capacity to ${capacityResult.failedRoomRateIds.length} room rate${capacityResult.failedRoomRateIds.length === 1 ? '' : 's'}.`,
      failedStage: 'roomRates',
      rollbackErrors,
      success: false,
    };
  }

  let taxesFeesSnapshot: PreExistingTaxesFeesSnapshot | undefined;

  if (taxesFeesDraft) {
    try {
      if (!wasPropertyCreatedThisSession) {
        taxesFeesSnapshot = await buildTaxesFeesSnapshot(propertyId, getProperty);
      }

      await updatePropertyTaxesAndFees(taxesFeesDraft, propertyId);
    } catch (error) {
      reportError(error);
      const rollbackErrors = await rollback(taxesFeesSnapshot);
      return {
        errorMessage: 'Unable to save taxes & fees.',
        failedStage: 'taxesFees',
        rollbackErrors,
        success: false,
      };
    }
  }

  const realRoomTypes = roomRateRooms.map((room) => ({
    id: resolveRoomKey(room.id, roomKeyToRealId),
    name: room.name,
  }));
  // Reused and newly-created room rates are treated alike here -- only rollback/capacity care
  // about the distinction.
  const linkResult = await linkPackages({
    appliedFlashRoomRates: roomRatesResult.createdRoomRates.concat(roomRatesResult.reusedRoomRates),
    flexibleNightsById,
    packageRoomTypeIds: Object.fromEntries(
      Object.entries(packageRoomTypeIds).map(([packageId, roomKey]) => [
        packageId,
        resolveRoomKey(roomKey, roomKeyToRealId),
      ]),
    ),
    propertyId,
    roomTypes: realRoomTypes,
    salesforceRooms,
    trimmedSalesforceOfferId: offerId,
  });

  if (linkResult.failures.length > 0) {
    // From linkResult's own fresh pre-write fetch, not the earlier preview -- the match can
    // differ by now (a Salesforce edit, or a different match once real room ids are used).
    const packageSnapshots = buildPackageSnapshots(linkResult.matchedPackages, offerId);
    // Only the packages linking actually wrote need undoing -- the rest were never touched.
    const { errors } = await rollbackCreatedFlashResources({
      createdResources,
      packageSnapshots: packageSnapshots.filter((snapshot) => linkResult.linkedPackageIds.includes(snapshot.packageId)),
      propertyId,
      taxesFeesSnapshot,
    });

    return {
      errorMessage: `Unable to link ${linkResult.failures.length} package${linkResult.failures.length === 1 ? '' : 's'} to a room and rate.`,
      failedStage: 'packages',
      packageLinkFailures: linkResult.failures,
      flexibleNightsBlockedCount: linkResult.flexibleNightsBlockedPackageIds.length,
      packageUnmatchedCount: linkResult.unmatchedPackageNames.length,
      rollbackErrors: errors,
      updateFailurePackageIds: linkResult.updateFailurePackageIds,
      success: false,
    };
  }

  return { success: true };
}
